import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardData } from '../../services/api';
import { CardContainer } from '../CardContainer';

interface RecentActivityListProps {
  activities: DashboardData['recent_activity'];
}

const ITEMS_PER_PAGE = 5;

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'visit_created':
      return 'clinical_notes';
    case 'sale_completed':
      return 'payments';
    case 'partial_payment':
      return 'account_balance_wallet';
    case 'patient_created':
      return 'person_add';
    default:
      return 'info';
  }
};

const getActivityColor = (type: string) => {
  switch (type) {
    case 'visit_created':
      return 'bg-blue-50 text-primary';
    case 'sale_completed':
      return 'bg-green-50 text-green-600';
    case 'partial_payment':
      return 'bg-orange-50 text-orange-500';
    case 'patient_created':
      return 'bg-purple-50 text-purple-600';
    default:
      return 'bg-slate-50 text-slate-600';
  }
};

const getActivityDescription = (activity: DashboardData['recent_activity'][0]) => {
  switch (activity.type) {
    case 'visit_created':
      return 'Nueva visita registrada';
    case 'sale_completed':
      return activity.amount
        ? `Venta completada - Q${activity.amount.toFixed(2)}`
        : 'Venta completada';
    case 'partial_payment':
      return activity.amount
        ? `Abono realizado - Q${activity.amount.toFixed(2)}`
        : 'Abono realizado';
    case 'patient_created':
      return 'Nuevo paciente registrado';
    default:
      return 'Actividad';
  }
};

const formatRelativeTime = (timestamp: string) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Hace un momento';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `Hace ${diffInMinutes} ${diffInMinutes === 1 ? 'minuto' : 'minutos'}`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `Hace ${diffInHours} ${diffInHours === 1 ? 'hora' : 'horas'}`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `Hace ${diffInDays} ${diffInDays === 1 ? 'día' : 'días'}`;
  }

  return date.toLocaleDateString('es-GT', {
    day: '2-digit',
    month: 'short',
  });
};

export const RecentActivityList = ({ activities }: RecentActivityListProps) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(activities.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const paginatedActivities = activities.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleActivityClick = (activity: DashboardData['recent_activity'][0]) => {
    if (activity.visit_id) {
      navigate(`/commercial-handoff/${activity.visit_id}`);
    } else {
      navigate(`/history/${activity.patient_id}`, {
        state: { patientName: activity.patient_name },
      });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (activities.length === 0) {
    return (
      <CardContainer>
        <h3 className="text-lg font-semibold text-[#101822] mb-4">Actividad Reciente</h3>
        <p className="text-sm text-slate-500 text-center py-8">No hay actividad reciente</p>
      </CardContainer>
    );
  }

  return (
    <CardContainer>
      <h3 className="text-lg font-semibold text-[#101822] mb-4">Actividad Reciente</h3>
      <div className="space-y-3">
        {paginatedActivities.map((activity, index) => (
          <div
            key={`${activity.type}-${activity.patient_id}-${activity.timestamp}-${index}`}
            className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
            onClick={() => handleActivityClick(activity)}
          >
            <div className={`p-2 rounded-lg ${getActivityColor(activity.type)}`}>
              <span className="material-symbols-outlined text-xl">{getActivityIcon(activity.type)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[#101822] truncate">{activity.patient_name}</p>
              <p className="text-xs text-slate-500">{getActivityDescription(activity)}</p>
            </div>
            <p className="text-xs text-slate-400 whitespace-nowrap">{formatRelativeTime(activity.timestamp)}</p>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              currentPage === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Anterior
          </button>

          <span className="text-xs text-slate-500">
            {currentPage + 1} / {totalPages}
          </span>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              currentPage === totalPages - 1
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Siguiente
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </CardContainer>
  );
};
